import React, { useState, useEffect, useRef } from 'react';
import { X, CheckCircle, Award, Heart, Shield, Lock, Phone, ShoppingBag } from 'lucide-react';
import './index.css';

// Main App Component
export default function App() {
  const [islandContent, setIsIslandContent] = useState(null);
  const [islandVisible, setIsIslandVisible] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Effect to handle the "dynamic island" behavior on scroll
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down, hide the island if it's not showing content
        if (!islandContent) {
          setIsIslandVisible(false);
        }
      } else {
        // Scrolling up or at the top, show the island
        setIsIslandVisible(true);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, islandContent]);

  const showIslandContent = (content) => {
    setIsIslandVisible(true);
    setIsIslandContent(content);
  };

  const closeIslandContent = () => {
    setIsIslandContent(null);
    // Keep it visible for a moment before letting the scroll handler hide it
    setTimeout(() => {
      if (window.scrollY > 100) {
        setIsIslandVisible(false);
      }
    }, 500);
  };

  return (
    <div className="app-container">
      {/* Dynamic Island Navbar */}
      <nav className={`navbar ${islandVisible ? 'navbar-visible' : 'navbar-hidden'}`}>
        <div className="navbar-inner">
          <a href="#hero" className="navbar-brand">AI Medic</a>
          
          {/* Always-visible Navigation Links */}
          <div className="navbar-links">
            <a href="#about" className="nav-link">About</a>
            <a href="#products" className="nav-link">Products</a>
            <a href="#why-us" className="nav-link">Why Us</a>
            <a href="#contact" className="nav-link">Contact</a>
          </div>
          
          <button onClick={() => showIslandContent(<ContactIsland />)} className="contact-button">
            <Phone size={16} />
          </button>
        </div>
      </nav>

      {/* Dynamic Island Content Overlay */}
      <div className={`island-overlay ${islandContent ? 'island-overlay-visible' : ''}`}>
        <div className="island-content-wrapper">
          <button onClick={closeIslandContent} className="island-close-button">
            <X size={24} />
          </button>
          {islandContent}
        </div>
      </div>

      {/* Hero Section with Looping Images */}
      <section id="hero" className="hero-section">
        <div className="hero-background-image">
          <div className="hero-overlay"></div>
        </div>
        <div className="hero-overlay">
          <h1 className="hero-title">
            Your AI mental health assistant is here!
          </h1>
          <p className="hero-subtitle">
            Personalized mental health guidance at your fingertips.
            Always here to listen, Always here to care
          </p>
          <a href="#products" className="hero-button">
            Explore Products
          </a>
        </div>
      </section>

      {/* Our Products Section */}
      <section id="products" className="products-section">
        <div className="container">
          <h2 className="section-title">Our Products</h2>
          <ProductDisplay />
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section">
        <div className="container">
          <h2 className="section-title">About Our Company</h2>
          <div className="about-content">
            <div className="about-image-wrapper">
              <img src="https://placehold.co/600x400/007678/ffffff?text=Cozy+Living" alt="About Us" className="about-image" />
            </div>
            <div className="about-text">
              <h3 className="about-heading">Crafting Your Space</h3>
              <p className="about-paragraph">
                At Luxe Living, we believe that a home is more than just a place to live—it's a reflection of your personality and a sanctuary for your life's moments. Our mission is to provide you with high-quality, stylish, and affordable furniture that transforms any space into a place you love.
              </p>
              <p className="about-paragraph">
                From elegant sofas to functional storage solutions, every piece in our collection is thoughtfully selected to inspire and delight. We are committed to exceptional customer service and helping you find exactly what you need to create your perfect home.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="why-us" className="why-us-section">
        <div className="container">
          <h2 className="section-title">Why Choose Us?</h2>
          <div className="why-us-grid">
            <WhyUsCard
              icon={<Award size={48} className="why-us-icon" />}
              title="Quality You Can Trust"
              description="We source only the finest materials to ensure our products are built to last."
            />
            <WhyUsCard
              icon={<Heart size={48} className="why-us-icon" />}
              title="Expertly Curated"
              description="Our collection is hand-picked by design professionals to bring you the latest trends."
            />
            <WhyUsCard
              icon={<Shield size={48} className="why-us-icon" />}
              title="Secure Shopping"
              description="Your data is safe with our advanced security protocols and encrypted payment gateways."
            />
            <WhyUsCard
              icon={<Lock size={48} className="why-us-icon" />}
              title="Zero Brokerage"
              description="We connect you directly to the best deals without any hidden fees or commissions."
            />
          </div>
        </div>
      </section>
      
      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="container">
          <h2 className="section-title">Get in Touch</h2>
          <div className="contact-form-wrapper">
            <p className="contact-intro-text">
              Have questions? We're here to help. Send us a message and we'll get back to you shortly.
            </p>
            <form onSubmit={(e) => { e.preventDefault(); showIslandContent(<SuccessMessage />); }} className="contact-form">
              <div>
                <label htmlFor="name" className="form-label">Name</label>
                <input type="text" id="name" name="name" className="form-input" required />
              </div>
              <div>
                <label htmlFor="email" className="form-label">Email</label>
                <input type="email" id="email" name="email" className="form-input" required />
              </div>
              <div>
                <label htmlFor="message" className="form-label">Message</label>
                <textarea id="message" name="message" rows="4" className="form-textarea" required></textarea>
              </div>
              <button type="submit" className="submit-button">
                Send Message
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-links">
            <a href="#about" className="footer-link">About Us</a>
            <a href="#products" className="footer-link">Our Products</a>
            <a href="#why-us" className="footer-link">Why Choose Us</a>
            <a href="#contact" className="footer-link">Contact</a>
          </div>
          <p className="footer-text">&copy; 2025 Luxe Living. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

// Sub-components

const ProductDisplay = () => {
  return (
    <div className="product-display-container">
      <div className="product-display-left">
        <h3 className="product-display-title">Fitness Smartwatch</h3>
        <p className="product-display-description">
          A fitness smartwatch is your ultimate wellness companion, tracking steps, heart rate, sleep patterns, and workouts in real-time. With smart notifications and GPS, it motivates you to stay active, achieve fitness goals, and maintain a healthier lifestyle seamlessly throughout your day.
        </p>
        <a href="#contact" className="product-display-button">
          <span>Contact us</span>
        </a>
      </div>
      <div className="product-display-right">
        <img
          src="https://placehold.co/600x400/805d7a/ffffff?text=Fitness+Smartwatch"
          alt="Fitness Smartwatch"
          className="product-display-image"
        />
      </div>
    </div>
  );
};


const WhyUsCard = ({ icon, title, description }) => (
  <div className="why-us-card">
    <div className="why-us-card-icon-wrapper">
      {icon}
    </div>
    <h3 className="why-us-card-title">{title}</h3>
    <p className="why-us-card-description">{description}</p>
  </div>
);

const ContactIsland = () => (
  <div className="contact-island">
    <h4 className="contact-island-title">Contact Us</h4>
    <p className="contact-island-description">
      Have a question? Call us or send an email. We're happy to help!
    </p>
    <div className="contact-details">
      <div className="contact-item">
        <Phone size={20} className="contact-icon" />
        <span className="contact-text">+91-9876543210</span>
      </div>
      <div className="contact-item">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-mail contact-icon">
          <rect width="20" height="16" x="2" y="4" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
        </svg>
        <span className="contact-text">info@luxeliving.in</span>
      </div>
    </div>
  </div>
);

const SuccessMessage = () => (
  <div className="success-message">
    <CheckCircle size={64} className="success-icon" />
    <h4 className="success-title">Message Sent!</h4>
    <p className="success-description">
      Thank you for reaching out. We will get back to you as soon as possible.
    </p>
  </div>
);